<template>
  <div class="campaign-message-container">
    <!-- Header -->
    <div class="header-section">
      <div class="header-info">
        <v-icon size="28" color="primary">mdi-message-draw</v-icon>
        <div class="ml-3">
          <h2 class="text-h5 font-weight-bold mb-0">Campaign Message</h2>
          <p class="text-caption grey--text mt-1">Compose and send a personalised message to your prospects</p>
        </div>
      </div>
      <div class="d-flex align-center">
        <v-btn color="primary" large class="mr-3" @click="openSelectDialog" :disabled="!userList.length">
          <v-icon left>mdi-send</v-icon> Send to Prospects
        </v-btn>
        <v-btn outlined color="primary" @click="openLoadMessageDialog">
          <v-icon left>mdi-folder-open</v-icon> Load Message
        </v-btn>
      </div>
    </div>

    <v-row>
      <!-- Message composer -->
      <v-col cols="12" md="5" lg="4">
        <v-card class="composer-card" elevation="2">
          <div class="composer-header">
            <v-icon color="primary">mdi-pencil-box</v-icon>
            <span class="font-weight-bold ml-2">Your Message</span>
          </div>
          <v-card-text>
            <v-text-field v-model="messageTitle" label="Title *" dense outlined class="mb-3" hide-details prepend-inner-icon="mdi-format-title"></v-text-field>
            <v-textarea v-model="messageContent" label="Message *" dense outlined rows="4" auto-grow class="mb-3" hide-details prepend-inner-icon="mdi-text"></v-textarea>
            <v-text-field v-model="messageLink" label="Link (optional)" dense outlined hide-details class="mb-3" prepend-inner-icon="mdi-link"></v-text-field>

            <div class="file-attachment-area">
              <div class="d-flex align-center mb-2">
                <v-icon color="primary" class="mr-1">mdi-paperclip</v-icon>
                <span class="text-caption font-weight-medium">Attachment</span>
              </div>
              <v-file-input v-model="selectedFile" dense outlined hide-details accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.mp4" prepend-icon="" label="Choose a file" @change="handleFileSelect" small></v-file-input>
              <div v-if="filePreviewUrl" class="file-preview-card mt-2">
                <v-img v-if="filePreviewType === 'image'" :src="filePreviewUrl" max-height="80" contain class="rounded" />
                <v-icon v-else size="40" color="primary">mdi-file-document</v-icon>
                <span class="text-caption ml-2">{{ selectedFile && selectedFile.name }}</span>
                <v-btn icon x-small color="error" @click="clearFile" class="ml-auto"><v-icon x-small>mdi-close</v-icon></v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Prospects list -->
      <v-col cols="12" md="7" lg="8">
        <v-card class="prospects-card" elevation="2">
          <div class="prospects-header">
            <div class="d-flex align-center">
              <v-icon color="primary">mdi-account-group</v-icon>
              <span class="font-weight-bold ml-2">{{ userList.length }} Prospects</span>
            </div>
            <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Filter" single-line hide-details dense outlined style="max-width: 220px;"></v-text-field>
          </div>

          <v-data-table :headers="headers" :items="userList" :items-per-page="10" :search="search" disable-sort class="clean-table" hide-default-footer disable-pagination fixed-header height="calc(100vh - 340px)">
            <template #[`item.name`]="{item}">
              <div class="prospect-name-cell">
                <v-avatar size="36" color="primary lighten-5" class="mr-3">
                  <span class="text-caption font-weight-bold primary--text">{{ item.name.charAt(0) }}</span>
                </v-avatar>
                <div>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div v-if="item.profession" class="text-caption grey--text">{{ item.profession }}</div>
                </div>
              </div>
            </template>
            <template #[`item.phone_number`]="{item}">
              <span class="text-caption">{{ item.phone_number || '—' }}</span>
            </template>
            <template #[`item.actions`]="{item}">
              <v-btn icon small color="green" @click="openWhatsAppDialog(item)" title="Send via WhatsApp"><v-icon small>mdi-whatsapp</v-icon></v-btn>
              <v-btn icon small color="blue darken-2" :disabled="!item.user_id" @click="openChatSingle(item)" title="Open chat"><v-icon small>mdi-message-text</v-icon></v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Multi‑select dialog -->
    <v-dialog v-model="selectDialog" max-width="650px">
      <v-card class="select-dialog-card">
        <v-card-title class="d-flex align-center"><v-icon color="primary" class="mr-2">mdi-check-circle</v-icon> Select Recipients</v-card-title>
        <v-card-text>
          <v-text-field v-model="selectSearch" label="Search prospects" prepend-inner-icon="mdi-magnify" single-line hide-details dense outlined class="mb-3"></v-text-field>
          <div class="selection-list">
            <div v-for="prospect in filteredUserList" :key="prospect.id" :class="['select-item', { selected: selectedProspects.includes(prospect) }]" @click="toggleSelection(prospect)">
              <v-icon small :color="selectedProspects.includes(prospect) ? 'primary' : '#ccc'">{{ selectedProspects.includes(prospect) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
              <span class="ml-2">{{ prospect.name }}</span>
              <span class="text-caption grey--text ml-2">{{ prospect.phone_number || '' }}</span>
            </div>
          </div>
          <p class="mt-3 text-caption font-weight-medium"><v-icon small color="primary">mdi-account-check</v-icon> {{ selectedProspects.length }} selected</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="selectDialog = false">Cancel</v-btn>
          <v-btn color="green" :disabled="!selectedProspects.length" @click="sendBulkWhatsApp"><v-icon left>mdi-whatsapp</v-icon> WhatsApp</v-btn>
          <v-btn color="blue darken-2" :disabled="!selectedProspects.length" @click="sendBulkChat"><v-icon left>mdi-message-text</v-icon> Chat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- WhatsApp warning dialog (same logic as MarketingResources) -->
    <v-dialog v-model="whatsappDialog" max-width="480px">
      <v-card class="dialog-card">
        <v-card-title class="dialog-header px-5 py-4">
          <v-avatar color="green lighten-5" size="40" class="me-3"><v-icon color="green">mdi-whatsapp</v-icon></v-avatar>
          <span class="text-h6 font-weight-bold">Send via WhatsApp</span>
          <v-spacer />
          <v-btn icon small @click="whatsappDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4 px-5">
          <v-alert type="warning" outlined dense class="mb-4 rounded-lg">
            <strong>Note:</strong> Direct file sending requires the WhatsApp Business API (not configured here). A download link will be sent instead.
          </v-alert>
          <p class="text-body-2 mb-1"><strong>Prospect:</strong> {{ currentProspect && currentProspect.name }}</p>
          <p class="text-body-2 mb-0"><strong>Message:</strong><br><code class="whatsapp-preview">{{ currentProspect ? buildTextMessage() : '' }}</code></p>
        </v-card-text>
        <v-card-actions class="px-5 py-4">
          <v-spacer />
          <v-btn text @click="whatsappDialog = false">Cancel</v-btn>
          <v-btn color="green darken-1" dark depressed @click="confirmWhatsAppSend">Send Message</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Load message dialog -->
    <v-dialog v-model="loadMessageDialog" max-width="500px">
      <v-card class="select-dialog-card">
        <v-card-title class="d-flex align-center"><v-icon color="primary" class="mr-2">mdi-message-bulleted</v-icon> Load Existing Message</v-card-title>
        <v-card-text>
          <v-text-field v-model="messageSearch" label="Search messages" prepend-inner-icon="mdi-magnify" single-line hide-details dense outlined class="mb-3"></v-text-field>
          <v-list dense class="message-list">
            <v-list-item v-for="msg in filteredMessageList" :key="msg.id" @click="loadSelectedMessage(msg)" class="select-item">
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ msg.title }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">{{ msg.content ? msg.content.substring(0, 80) + '…' : '' }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small @click="loadSelectedMessage(msg)"><v-icon small color="primary">mdi-arrow-right</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <p v-if="!filteredMessageList.length" class="text-center text-caption grey--text mt-2">No messages found</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="loadMessageDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CampaignMessage',
  data() {
    return {
      userList: [],
      search: '',
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Phone', value: 'phone_number' },
        { text: '', value: 'actions', sortable: false, width: '100px' },
      ],

      messageTitle: '',
      messageContent: '',
      messageLink: '',
      selectedFile: null,
      filePreviewUrl: '',
      filePreviewType: '',

      selectDialog: false,
      selectSearch: '',
      selectedProspects: [],

      whatsappDialog: false,
      currentProspect: null,

      loadMessageDialog: false,
      messageSearch: '',
      messageList: [],
    }
  },
  computed: {
    filteredUserList() {
      if (!this.selectSearch) return this.userList
      const q = this.selectSearch.toLowerCase()
      return this.userList.filter(p => p.name.toLowerCase().includes(q))
    },
    filteredMessageList() {
      if (!this.messageSearch) return this.messageList
      const q = this.messageSearch.toLowerCase()
      return this.messageList.filter(m => m.title.toLowerCase().includes(q))
    },
  },
  async mounted() {
    await this.loadProspects()
    await this.loadMessagesList()
    document.title = 'TopUp | Campaign Message'
  },
  methods: {
    async loadProspects() {
  try {
    const rawGroupId = this.$route.params.group_id
    // Validation : on n'appelle pas l'API si le paramètre est absent, 'undefined', 'null' ou non numérique
    if (!rawGroupId || rawGroupId === 'undefined' || rawGroupId === 'null' || isNaN(Number(rawGroupId))) {
      this.showToast('No valid audience selected. Please go back and choose a list.', 'error')
      return
    }
    const group_id = Number(rawGroupId)
    const message_id = this.$route.params.message_id || '0'
    const message_means = this.$route.params.message_means || 'Whatsapp'

    const formData = new FormData()
    formData.append('group_id', group_id)
    formData.append('message_id', message_id)
    formData.append('message_means', message_means)

    const response = await axios.post('/api/v1/topup/get/prospects/ad_message/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    this.userList = response.data.prospects.map(p => ({
      ...p,
      user_id: p.user_id || null,
    }))
  } catch (error) {
    this.showToast('Failed to load prospects', 'error')
  }
},
    async loadMessagesList() {
      try {
        const res = await axios.get('/api/v1/topup/get/admessages/select/')
        this.messageList = res.data
      } catch (e) { console.error('Failed to load messages', e) }
    },
    openLoadMessageDialog() {
      this.loadMessageDialog = true
      this.messageSearch = ''
    },
    loadSelectedMessage(msg) {
      this.messageTitle = msg.title || ''
      this.messageContent = msg.content || ''
      this.messageLink = msg.link || ''
      this.loadMessageDialog = false
      this.showToast('Message loaded', 'success')
    },

    handleFileSelect(file) {
      if (!file) { this.clearFile(); return }
      this.selectedFile = file
      const reader = new FileReader()
      reader.onload = (e) => {
        this.filePreviewUrl = e.target.result
        this.filePreviewType = file.type.startsWith('image/') ? 'image' : 'file'
      }
      reader.readAsDataURL(file)
    },
    clearFile() {
      this.selectedFile = null
      this.filePreviewUrl = ''
      this.filePreviewType = ''
    },

    // ---- WHATSAPP (with popup) ----
    openWhatsAppDialog(prospect) {
      this.currentProspect = prospect
      this.whatsappDialog = true
    },
    confirmWhatsAppSend() {
      if (!this.currentProspect) return
      const phone = this.formatPhone(this.currentProspect.phone_number)
      if (!phone) {
        this.showToast('Invalid phone number', 'warning')
        this.whatsappDialog = false
        return
      }
      const fullMessage = this.buildTextMessage()
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(fullMessage)}`, '_blank')
      this.whatsappDialog = false
    },

    // ---- CHAT (same logic as TopUpClientsTable) ----
    openChatSingle(prospect) {
      if (!prospect.user_id) return
      this.$router.push({ name: 'topup-chat', query: { client: prospect.user_id } })
    },

    // ---- BULK ----
    sendBulkWhatsApp() {
      this.selectedProspects.forEach(p => {
        const phone = this.formatPhone(p.phone_number)
        if (phone) {
          const fullMessage = this.buildTextMessage()
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(fullMessage)}`, '_blank')
        }
      })
      this.selectDialog = false
    },
    async sendBulkChat() {
      const chatable = this.selectedProspects.filter(p => p.user_id)
      for (const prospect of chatable) {
        try {
          const fd = new FormData()
          fd.append('downline_id', prospect.user_id)
          fd.append('message', this.buildTextMessage())
          if (this.selectedFile) {
            const mimeType = this.selectedFile.type
            let messageType = 'file'
            if (mimeType.startsWith('image/')) messageType = 'image'
            else if (mimeType.startsWith('video/')) messageType = 'video'
            fd.append('message_type', messageType)
            fd.append('file', this.selectedFile, this.selectedFile.name)
          } else {
            fd.append('message_type', 'text')
          }
          await axios.post('/api/v1/topup/send_to_downline/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } catch (e) { console.error('Failed for', prospect.name, e) }
      }
      this.selectDialog = false
      this.showToast(`Message sent to ${chatable.length} user(s) via Chat`, 'success')
    },

    buildTextMessage() {
      let msg = ''
      if (this.messageTitle) msg += `*${this.messageTitle}*\n\n`
      if (this.messageContent) msg += `${this.messageContent}\n\n`
      if (this.messageLink) msg += `${this.messageLink}`
      return msg.trim()
    },
    formatPhone(raw) {
      if (!raw) return ''
      let digits = raw.replace(/\D/g, '')
      if (digits.startsWith('0')) digits = digits.substring(1)
      if (digits.length === 9) digits = '237' + digits
      return digits ? `+${digits}` : ''
    },

    openSelectDialog() {
      this.selectedProspects = []
      this.selectDialog = true
    },
    toggleSelection(prospect) {
      const index = this.selectedProspects.indexOf(prospect)
      index === -1 ? this.selectedProspects.push(prospect) : this.selectedProspects.splice(index, 1)
    },
    showToast(msg, type) {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', type === 'success' ? 'teal darken-4' : 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
.campaign-message-container { padding: 1rem; }
.header-section { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; gap: 1rem; }
.composer-card { border-radius: 12px; height: 100%; }
.composer-header { display: flex; align-items: center; padding: 12px 16px; background: #f4f6f8; border-bottom: 1px solid #eee; border-radius: 12px 12px 0 0; }
.prospects-card { border-radius: 12px; overflow: hidden; }
.prospects-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: #f4f6f8; border-bottom: 1px solid #eee; }
.prospect-name-cell { display: flex; align-items: center; }
.file-preview-card { display: flex; align-items: center; padding: 8px; background: #fafafa; border: 1px dashed #ccc; border-radius: 8px; }
.clean-table { border: none; }
.clean-table :deep(.v-data-table-header) { background: transparent; }
.clean-table :deep(th) { font-size: 0.8rem; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
.selection-list { max-height: 300px; overflow-y: auto; border: 1px solid #eee; border-radius: 8px; padding: 8px; }
.select-item { display: flex; align-items: center; padding: 8px 12px; cursor: pointer; border-radius: 6px; transition: background 0.15s; margin-bottom: 4px; }
.select-item:hover { background: #f5f5f5; }
.select-item.selected { background: rgba(25, 118, 210, 0.08); }
.message-list { max-height: 300px; overflow-y: auto; border: 1px solid #eee; border-radius: 8px; }
.dialog-card { border-radius: 16px; overflow: hidden; }
.dialog-header { background: #fff; }
.whatsapp-preview { display: block; white-space: pre-wrap; word-break: break-word; color: #555; font-family: 'Roboto Mono', monospace; font-size: 0.85rem; }
@media (max-width: 600px) {
  .campaign-message-container { padding: 0.5rem; }
  .prospects-header { flex-direction: column; align-items: flex-start; gap: 0.5rem; }
}
</style>
